import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import { createModal } from '@codegouvfr/react-dsfr/Modal';
import { useIsModalOpen } from '@codegouvfr/react-dsfr/Modal/useIsModalOpen';
import ToggleSwitch from '@codegouvfr/react-dsfr/ToggleSwitch';
import clsx from 'clsx';
import { uniqBy } from 'lodash-es';
import {
  type MatrixKind,
  matrixKindLabel,
  matrixLabel
} from 'maestro-shared/schema/Matrix/MatrixReferential';
import { getSubPlanMatrixKinds } from 'maestro-shared/schema/ProgrammingPlan/SubPlanMatrices';
import type { SubstanceKind } from 'maestro-shared/schema/Substance/SubstanceKind';
import { SubstanceKindLabels } from 'maestro-shared/schema/Substance/SubstanceKind';
import {
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import BackToTopButton from 'src/components/BackToTopButton/BackToTopButton';
import { LaboratoryAgreementDetailProvider } from 'src/components/LaboratoryAgreement/LaboratoryAgreementDetailModal/LaboratoryAgreementDetailContext';
import { useMatrixReferential } from 'src/hooks/useMatrixReferential';
import { ApiClientContext } from '../../services/apiClient';
import { getLaboratoryAgreementsExportURL } from '../../services/laboratory.service';
import { groupSubstanceKindsBySample } from '../../utils/sampleLaboratories';
import { pluralize } from '../../utils/stringUtils';
import LaboratoryAgreementsModal from './LaboratoryAgreementsModal/LaboratoryAgreementsModal';
import LaboratoryAgreementsTable, {
  type AgreementRow,
  isRowChecked,
  substanceKindsLabel,
  toRowKey
} from './LaboratoryAgreementsTable/LaboratoryAgreementsTable';
import './LaboratoryAgreementsView.scss';
import type {
  LaboratoryAgreement,
  LaboratoryAgreementField,
  LaboratoryAgreementRowKey
} from 'maestro-shared/schema/Laboratory/LaboratoryAgreement';
import {
  type ProgrammingSubPlan,
  type ProgrammingSubPlanId,
  subPlanLabel
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import { assert, type Equals } from 'tsafe';
import { AppPageWithYearTitle } from '../../components/_app/AppPage/AppPageWithYearTitle';

const agreementsModal = createModal({
  id: 'laboratory-agreements-modal',
  isOpenedByDefault: false
});

type Props = {
  year: number;
};

const LaboratoryAgreements = ({ year, ..._rest }: Props) => {
  assert<Equals<keyof typeof _rest, never>>();

  const apiClient = useContext(ApiClientContext);
  const matrixReferential = useMatrixReferential();
  const [selectedStringRowKeys, setSelectedStringRowKeys] = useState<string[]>(
    []
  );
  const [modalRowKeys, setModalRowKeys] = useState<LaboratoryAgreementRowKey[]>(
    []
  );
  const [modalAgreements, setModalAgreements] = useState<
    Pick<
      LaboratoryAgreement,
      | 'laboratoryId'
      | 'referenceLaboratory'
      | 'detectionAnalysis'
      | 'confirmationAnalysis'
    >[]
  >([]);
  const [modalProgrammingSubPlan, setModalProgrammingSubPlan] =
    useState<ProgrammingSubPlan>();
  const [substanceFilter, setSubstanceFilter] = useState<SubstanceKind[]>([]);
  const [labFilter, setLabFilter] = useState<string[]>([]);
  const [labAgreementTypeFilter, setLabAgreementTypeFilter] = useState<
    LaboratoryAgreementField[]
  >([]);
  const [matrixCombinedFilter, setMatrixCombinedFilter] = useState<string[]>(
    []
  );
  const [subPlanFilter, setSubPlanFilter] = useState<ProgrammingSubPlanId[]>(
    []
  );
  const [showWithoutLab, setShowWithoutLab] = useState(false);

  const { data: agreements = [] } = apiClient.useFindLaboratoryAgreementsQuery({
    year
  });
  const { data: checks = [] } = apiClient.useFindLaboratoryAgreementChecksQuery(
    { year }
  );
  const { data: allProgrammingPlans = [] } =
    apiClient.useFindProgrammingPlansQuery({});
  const programmingPlans = useMemo(
    () => allProgrammingPlans.filter((p) => p.year === year),
    [allProgrammingPlans, year]
  );

  const rows = useMemo(
    () =>
      programmingPlans.flatMap((plan) =>
        plan.subPlans.flatMap((subPlan) =>
          groupSubstanceKindsBySample(
            subPlan.samples,
            subPlan.substanceKinds ?? []
          ).map((substanceKinds) => ({
            programmingSubPlan: subPlan,
            programmingPlanYear: plan.year,
            substanceKinds,
            laboratories: agreements.filter(
              (a) =>
                a.programmingSubPlanId === subPlan.id &&
                substanceKinds.includes(a.substanceKind)
            )
          }))
        )
      ),
    [agreements, programmingPlans]
  );

  const rowsWithLab = useMemo(
    () =>
      rows.filter((r) =>
        r.laboratories.some(
          (lab) =>
            lab.referenceLaboratory ||
            lab.detectionAnalysis ||
            lab.confirmationAnalysis
        )
      ).length,
    [rows]
  );
  const rowsWithoutLab = rows.length - rowsWithLab;
  const { data: laboratories = [] } = apiClient.useFindLaboratoriesQuery({});
  const [updateAgreements] = apiClient.useUpdateLaboratoryAgreementsMutation();
  const [updateCheck] = apiClient.useUpdateLaboratoryAgreementCheckMutation();

  const subPlanOptions = useMemo(
    () =>
      [...new Set(rows.map((r) => r.programmingSubPlan.id))].map((value) => {
        const row = rows.find((r) => r.programmingSubPlan.id === value);
        return {
          value,
          label: row?.programmingSubPlan.subPlanNumber ?? value
        };
      }),
    [rows]
  );

  const substanceOptions = useMemo(
    () =>
      [...new Set(rows.flatMap((r) => r.substanceKinds))]
        .map((value) => ({
          value,
          label: SubstanceKindLabels[value]
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    [rows]
  );

  const labsInRows = useMemo(
    () =>
      new Set(rows.flatMap((r) => r.laboratories.map((l) => l.laboratoryId))),
    [rows]
  );

  const labOptions = useMemo(
    () =>
      laboratories
        .toSorted((a, b) => a.shortName.localeCompare(b.shortName))
        .map((l) => ({
          value: l.id,
          label: l.shortName,
          disabled: !labsInRows.has(l.id)
        })),
    [laboratories, labsInRows]
  );

  const matrixCombinedOptions = useMemo(() => {
    const subPlans = programmingPlans.flatMap((plan) => plan.subPlans);

    const kindOptions = [
      ...new Set(
        subPlans.flatMap((subPlan) => getSubPlanMatrixKinds(subPlan.matrices))
      )
    ]
      .map((kind) => ({
        value: `kind:${kind}`,
        label: matrixKindLabel(matrixReferential, kind)
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label));

    const detailedMatrices = subPlans.flatMap((subPlan) =>
      (subPlan.matrices?.items ?? []).flatMap(({ matrixKind, matrices }) =>
        matrices.map((matrix) => ({ matrixKind, matrix }))
      )
    );

    const specificMatrixOptions = uniqBy(detailedMatrices, 'matrix')
      .map(({ matrixKind, matrix }) => ({
        value: `matrix:${matrix}`,
        label: `${matrixKindLabel(matrixReferential, matrixKind)} > ${matrixLabel(matrixReferential, matrix)}`
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label));

    return [...kindOptions, ...specificMatrixOptions];
  }, [programmingPlans, matrixReferential]);

  const filteredRows = useMemo(() => {
    const checksSet = new Set(
      checks.map((c) => `${c.programmingSubPlanId}_${c.substanceKind}`)
    );

    return rows
      .filter(
        (r) =>
          (subPlanFilter.length === 0 ||
            subPlanFilter.includes(r.programmingSubPlan.id)) &&
          (substanceFilter.length === 0 ||
            r.substanceKinds.some((substanceKind) =>
              substanceFilter.includes(substanceKind)
            )) &&
          (matrixCombinedFilter.length === 0 ||
            matrixCombinedFilter.some(
              (v) =>
                (v.startsWith('kind:') &&
                  r.programmingSubPlan.matrices?.items.some(
                    (item) => item.matrixKind === v.slice(5)
                  )) ||
                (v.startsWith('matrix:') &&
                  r.programmingSubPlan.matrices?.items.some((item) =>
                    item.matrices.some((matrix) => matrix === v.slice(7))
                  ))
            )) &&
          ((labFilter.length === 0 && labAgreementTypeFilter.length === 0) ||
            r.laboratories.some(
              (l) =>
                (labFilter.length === 0 ||
                  labFilter.includes(l.laboratoryId)) &&
                (labAgreementTypeFilter.length === 0 ||
                  labAgreementTypeFilter.some((field) => l[field]))
            )) &&
          (!showWithoutLab ||
            !r.laboratories.some(
              (l) =>
                l.referenceLaboratory ||
                l.detectionAnalysis ||
                l.confirmationAnalysis
            ))
      )
      .sort((a, b) => {
        const aChecked = isRowChecked(checksSet, a);
        const bChecked = isRowChecked(checksSet, b);
        if (aChecked !== bChecked) {
          return aChecked ? 1 : -1;
        }
        const substanceCmp = substanceKindsLabel(a).localeCompare(
          substanceKindsLabel(b)
        );
        if (substanceCmp !== 0) {
          return substanceCmp;
        }
        return subPlanLabel(
          matrixReferential,
          a.programmingSubPlan
        ).localeCompare(subPlanLabel(matrixReferential, b.programmingSubPlan));
      });
  }, [
    rows,
    subPlanFilter,
    substanceFilter,
    matrixCombinedFilter,
    labFilter,
    labAgreementTypeFilter,
    showWithoutLab,
    checks,
    matrixReferential
  ]);

  const deferredFilteredRows = useDeferredValue(filteredRows);

  const allSelected =
    filteredRows.length > 0 &&
    selectedStringRowKeys.length === filteredRows.length;

  const rowAgreementsSignature = (labs: LaboratoryAgreement[]) =>
    labs
      .map(
        (l) =>
          `${l.laboratoryId}:${l.referenceLaboratory ? 1 : 0}${l.detectionAnalysis ? 1 : 0}${l.confirmationAnalysis ? 1 : 0}`
      )
      .sort()
      .join(',');

  const selectedRows = useMemo(
    () =>
      filteredRows.filter((r) => selectedStringRowKeys.includes(toRowKey(r))),
    [filteredRows, selectedStringRowKeys]
  );

  const selectedRowsConsistent =
    selectedRows.length <= 1 ||
    selectedRows.every(
      (r) =>
        rowAgreementsSignature(r.laboratories) ===
        rowAgreementsSignature(selectedRows[0].laboratories)
    );

  const isDetailModalOpen = useRef(false);

  const handleOpenModal = useCallback(() => {
    const firstRow = selectedRows[0];
    setModalAgreements(firstRow?.laboratories ?? []);
    setModalRowKeys(
      selectedRows.flatMap((row) =>
        row.substanceKinds.map((substanceKind) => ({
          programmingSubPlanId: row.programmingSubPlan.id,
          substanceKind
        }))
      )
    );
    setModalProgrammingSubPlan(firstRow.programmingSubPlan);
    agreementsModal.open();
  }, [selectedRows]);

  const handleOpenModalForRow = useCallback((row: AgreementRow) => {
    setModalAgreements(row.laboratories);
    setModalRowKeys(
      row.substanceKinds.map((substanceKind) => ({
        programmingSubPlanId: row.programmingSubPlan.id,
        substanceKind
      }))
    );
    setModalProgrammingSubPlan(row.programmingSubPlan);
    agreementsModal.open();
  }, []);

  const handleToggleRow = useCallback(
    (key: string) =>
      setSelectedStringRowKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      ),
    []
  );

  const handleToggleAll = useCallback(
    () =>
      setSelectedStringRowKeys((prev) =>
        prev.length === filteredRows.length ? [] : filteredRows.map(toRowKey)
      ),
    [filteredRows]
  );

  const handleDeselect = useCallback(() => setSelectedStringRowKeys([]), []);

  useEffect(() => {
    setSelectedStringRowKeys([]);
  }, [
    subPlanFilter,
    substanceFilter,
    matrixCombinedFilter,
    labFilter,
    labAgreementTypeFilter,
    showWithoutLab
  ]);

  useIsModalOpen(agreementsModal, {
    onConceal: () => {
      if (isDetailModalOpen.current) {
        return;
      }
      setSelectedStringRowKeys([]);
      setModalRowKeys([]);
    }
  });

  return (
    <LaboratoryAgreementDetailProvider
      onOpen={() => {
        isDetailModalOpen.current = true;
      }}
      onConceal={() => {
        isDetailModalOpen.current = false;
      }}
      onSave={async (updated) => {
        await updateAgreements({
          laboratoryId: updated.laboratoryId,
          laboratoryAgreementRowKey: {
            programmingSubPlanId: updated.programmingSubPlanId,
            substanceKind: updated.substanceKind
          },
          referenceLaboratory: updated.referenceLaboratory,
          detectionAnalysis: updated.detectionAnalysis,
          confirmationAnalysis: updated.confirmationAnalysis
        }).unwrap();
        setModalAgreements((prev) =>
          prev.some((a) => a.laboratoryId === updated.laboratoryId)
            ? prev.map((a) =>
                a.laboratoryId === updated.laboratoryId
                  ? {
                      laboratoryId: updated.laboratoryId,
                      referenceLaboratory: updated.referenceLaboratory,
                      detectionAnalysis: updated.detectionAnalysis,
                      confirmationAnalysis: updated.confirmationAnalysis
                    }
                  : a
              )
            : [
                ...prev,
                {
                  laboratoryId: updated.laboratoryId,
                  referenceLaboratory: updated.referenceLaboratory,
                  detectionAnalysis: updated.detectionAnalysis,
                  confirmationAnalysis: updated.confirmationAnalysis
                }
              ]
        );
      }}
    >
      <div
        className={clsx(
          cx('fr-text--lg', 'fr-text--regular', 'fr-hint-text', 'fr-mb-0'),
          'laboratory-agreements-counts',
          'd-flex-align-center'
        )}
      >
        <span>{pluralize(rows.length, { preserveCount: true })('ligne')}</span>
        <span
          className={cx(
            'fr-icon-checkbox-circle-line',
            'fr-label--success',
            'fr-icon--sm',
            'fr-ml-2w'
          )}
          aria-hidden="true"
        />
        <span className={cx('fr-ml-2v')}>{rowsWithLab} avec laboratoire</span>
        <span
          className={cx(
            'fr-icon-time-line',
            'fr-label--error',
            'fr-icon--sm',
            'fr-ml-2w'
          )}
          aria-hidden="true"
        />
        <span className={cx('fr-ml-2v')}>
          {rowsWithoutLab} sans laboratoire
        </span>
      </div>
      <div className={clsx('white-container', cx('fr-p-4w'))}>
        <div
          className={clsx(
            cx('fr-mb-3w'),
            'd-flex-align-center',
            'd-flex-justify-end',
            'no-wrap'
          )}
        >
          <ToggleSwitch
            label="Lignes sans laboratoires"
            checked={showWithoutLab}
            onChange={setShowWithoutLab}
            showCheckedHint={false}
            disabled={rowsWithoutLab === 0}
          />
          <Button
            iconId="fr-icon-file-download-line"
            priority="secondary"
            onClick={() =>
              window.open(
                getLaboratoryAgreementsExportURL({
                  year,
                  programmingSubPlanIds: subPlanFilter.length
                    ? subPlanFilter
                    : undefined,
                  substanceKinds: substanceFilter.length
                    ? substanceFilter
                    : undefined,
                  laboratoryIds: labFilter.length ? labFilter : undefined,
                  matrixKinds: matrixCombinedFilter
                    .filter((v) => v.startsWith('kind:'))
                    .map((v) => v.slice(5) as MatrixKind).length
                    ? matrixCombinedFilter
                        .filter((v) => v.startsWith('kind:'))
                        .map((v) => v.slice(5) as MatrixKind)
                    : undefined,
                  withoutLab: showWithoutLab || undefined
                })
              )
            }
            className={cx('fr-ml-3w')}
          >
            Exporter
          </Button>
        </div>
        <LaboratoryAgreementsTable
          rows={deferredFilteredRows}
          selectedStringRowKeys={selectedStringRowKeys}
          allSelected={allSelected}
          selectedRowsConsistent={selectedRowsConsistent}
          checks={checks}
          laboratories={laboratories}
          kindFilter={subPlanFilter}
          kindOptions={subPlanOptions}
          onKindFilterChange={setSubPlanFilter}
          substanceFilter={substanceFilter}
          substanceOptions={substanceOptions}
          onSubstanceFilterChange={setSubstanceFilter}
          matrixCombinedFilter={matrixCombinedFilter}
          matrixCombinedOptions={matrixCombinedOptions}
          onMatrixCombinedFilterChange={setMatrixCombinedFilter}
          labFilter={labFilter}
          labOptions={labOptions}
          onLabFilterChange={setLabFilter}
          labAgreementTypeFilter={labAgreementTypeFilter}
          onLabAgreementTypeFilterChange={setLabAgreementTypeFilter}
          onToggleRow={handleToggleRow}
          onToggleAll={handleToggleAll}
          onDeselect={handleDeselect}
          onOpenModal={handleOpenModal}
          onOpenModalForRow={handleOpenModalForRow}
          onUpdateCheck={updateCheck}
        />
      </div>
      <LaboratoryAgreementsModal
        modal={agreementsModal}
        laboratoryAgreementRowKeys={modalRowKeys}
        agreements={modalAgreements}
        laboratories={laboratories}
        programmingSubPlan={modalProgrammingSubPlan}
        onSave={async (laboratoryId, input) => {
          await updateAgreements({ laboratoryId, ...input }).unwrap();
        }}
      />
      <BackToTopButton />
    </LaboratoryAgreementDetailProvider>
  );
};

export const LaboratoryAgreementsView = ({
  ..._rest
}: Record<never, never> = {}) => {
  assert<Equals<keyof typeof _rest, never>>();

  return (
    <AppPageWithYearTitle
      title="Agréments laboratoires"
      render={(year) => <LaboratoryAgreements year={year} />}
    />
  );
};
